import { predictiveBundleByProductId } from '../api/bundle';
import { CartAPI } from '../api/cart';
import { getTemplateByIds } from '../api/template';
import { KEYS } from '../constants/keys';
import { setUniformProductImageHeight } from '../product-item/index.js';
import {
  buildUrlWithLocalization,
  formatCurrency,
  getSessionStorage,
  getThemeSettings,
  handleClickTagAItemBuildUrlWithLocale,
  isSearchPage,
  setSessionStorage,
  setWindowLocation,
  splitGoogleFontStyle,
} from '../utils';
import { isKeyboardActivated } from '../utils/keyboardActivated.js';
import { BundleEventProvider } from './event';

const DYNAMIC_BUNDLE = 'dynamic-bundle';
const KEY_DYNAMIC_BUNDLE = 'dynamicBundleKey';
const PRE_FIX_CLASS_DYNAMIC_BUNDLE_MODAL = 'boost-sd__dynamic-bundle-modal-';
const MODAL_WRAPPER_SELECTOR = '.boost-sd__modal-wrapper';
const RECOMMENDATION_SELECTOR = '.boost-sd__recommendation';
const DYNAMIC_BUNDLE_ID_CONTAINER_SEARCH_PAGE = 'boost-sd__dynamic-bundle-search-page';
const EMBEDDED_BUNDLE_ID_CONTAINER_PRODUCT = 'boost-sd__embedded-bundle-product';
const CHECK_BOX_SELECTOR = '.boost-sd__recommendation-bundle-checkbox';
const CHECK_BOX_BUTTON = '.boost-sd__recommendation-bundle-checkbox-box';
const DROPDOWN_ITEM_SELECTOR = '.boost-sd__dropdown-dynamic-item';
const BUNDLE_IMAGE_ITEM_SELECTOR = '.boost-sd__recommendation-bundle-image-item';
const DYNAMIC_BUNDLE_SHOW_POPUP_SELECTOR = '.boost-sd__recommendation-dynamic-show-popup';
const BUTTON_ADD_BUNDLE_TO_CART_SELECTOR = `.boost-sd__btn-add-to-cart`;
const BUNDLE_ACTION = {
  initData: 'init-data',
  changeVariant: 'change-variant',
  clickCheckbox: 'click-checkbox',
};
const DROPDOWN_LIST = 'boost-sd__dropdown-option-list';
const DROPDOWN_LIST_OPEN = 'boost-sd__dropdown-option-list--open';
const DROPDOWN_SELECT_OPTION = 'boost-sd__dropdown-selected-option';
const DROPDOWN_CONTAINER = 'boost-sd__dropdown-dynamic';
const DROPDOWN_SELECT_OPTION_SELECTED = 'boost-sd__dropdown-option--selected';
const DYNAMIC_BUNDLE_DEFAULT_SETTINGS = {
  titleName: 'Buy more and save',
  templateType: 'default',
  titleAlignment: 'center',
  titleTextColor: '#3D4246',
  titleFont: 'Poppins',
  titleTextTransform: 'capitalize',
  titleFontStyle: 'regular',
  titleFontSize: 14,
  buttonBackgroundColor: '#111111',
  buttonText: 'Add bundle to cart',
  buttonTextColor: '#FFFFFF',
  isDefault: false,
};
const PREDICTIVE_BUNDLE_BLOCK_SELECTOR = '.boost-sd-widget-predictive-bundle';
const BUNDLE_NAME_TEMPLATE = {
  dynamic: 'recommendationDynamicBundle',
  embedded: 'recommendationEmbeddedBundle',
};
export const KEY_DYNAMIC_BUNDLE_DATA_SESSION = `DynamicBundleDataSession`;
const KEY_DYNAMIC_BUNDLE_OPENING = `DynamicBundleOpening`;
const KEY_DYNAMIC_BUNDLE_CHECKBOX = `DynamicBundleCheckBox`;
const KEY_DYNAMIC_BUNDLE_COMBOBOX_BUTTON = `DynamicBundleComboBox`;

/**
 * @type {import('./index.d').formatPrice}
 */
export const formatPrice = (context, price) => {
  const { showCentAsSuperscript, showCurrencyCodes } =
    context.templateSettings?.themeSettings?.productItems?.productInfo?.elements?.price || {};
  return formatCurrency({
    context,
    value: Number(price),
    showCurrencyCodes,
    showCentAsSuperscript,
    removeDecimalPoint: showCentAsSuperscript,
  });
};

/**
 * @type {import('./index.d').isValidateDynamicBundle}
 */
export const isValidateDynamicBundle = (context) => {
  // If there is no widgetId => do nothing
  if (!context || !context.widgetId || context.widgetId === '') return false;

  // If there is no recommendationWidgets => do nothing
  const recommendationWidgets = context.app?.recommendationWidgets;
  if (!recommendationWidgets || Object.keys(recommendationWidgets).length === 0) return false;

  const dynamicBundleWidgets = recommendationWidgets?.[DYNAMIC_BUNDLE];

  if (!dynamicBundleWidgets || Object.keys(dynamicBundleWidgets).length === 0) return false;

  return true;
};

/**
 * @type {import('./index.d').getDynamicBundleWidgetTemplate}
 */
const getDynamicBundleWidgetTemplate = async (context, name = 'recommendationDynamicBundle') => {
  const { templates = {} } = await getTemplateByIds(
    context,
    context.app.templateMetadata?.themeNameLib,
    [
      {
        name: name,
        id: context.app.templateMetadata?.[name],
      },
    ]
  );

  return templates[context.app.templateMetadata?.[name]] || '';
};

/**
 * @type {import('./index.d').getDynamicBundleDataByWidgetId}
 */
const getDynamicBundleDataByWidgetId = (context, widgetId, isPopupDynamicBundleOpening) => {
  const [getDynamicBundleData] = context.useContextState(`${KEY_DYNAMIC_BUNDLE}-${widgetId}`, {});

  return {
    ...getDynamicBundleData(),
    isPopupDynamicBundleOpening,
  };
};

/**
 * @type {import('./index.d').renderDynamicBundleWithData}
 */
const renderDynamicBundleWithData = async (
  context,
  widgetId,
  dynamicBundleData,
  wrapper,
  name,
  callBack
) => {
  const templateDynamicBundle = await getDynamicBundleWidgetTemplate(context, name);
  const html = await context.templateRender(templateDynamicBundle, {
    bundle: dynamicBundleData,
    ...getDynamicBundleSettingsByWidgetId(context, widgetId),
    locale: (window?.Shopify?.routes?.root || '/')?.replace(/\/$/, ''),
  });

  if (wrapper) {
    wrapper.innerHTML = html;
    const bundleModal = document
      ?.getElementById(`${PRE_FIX_CLASS_DYNAMIC_BUNDLE_MODAL}${context.blockType}`)
      ?.querySelector(MODAL_WRAPPER_SELECTOR);
    if (bundleModal) {
      trapFocusInBundle(bundleModal, context, true);
    }
  } else if (dynamicBundleData?.isPopupDynamicBundleOpening) {
    const bundleModal = document
      ?.getElementById(`${PRE_FIX_CLASS_DYNAMIC_BUNDLE_MODAL}${context.blockType}`)
      ?.querySelector(MODAL_WRAPPER_SELECTOR);

    if (bundleModal) {
      bundleModal.innerHTML = html;
      bundleModal.style.display = 'block';
      trapFocusInBundle(bundleModal, context, true);
    }
  } else {
    const targetElementByContext = getTargetElementUpdate(context);
    if (targetElementByContext) {
      targetElementByContext.innerHTML = html;
      trapFocusInBundle(targetElementByContext, context, false);
    }
  }

  if (typeof callBack === 'function') {
    callBack();
  }
};

/**
 * @type {import('./index.d').addBundleToCart}
 */
const addBundleToCart = async (context, target) => {
  const widgetId = target?.closest(RECOMMENDATION_SELECTOR)?.dataset?.widgetId;

  const dynamicBundleData = getDynamicBundleDataByWidgetId(context, widgetId);

  const data = [];

  const _boostBundleProducts = dynamicBundleData?.products
    ?.map((product) => product.selectedOption.id)
    .join(',');

  if (dynamicBundleData?.products) {
    for (const product of dynamicBundleData.products) {
      if (!product.checked) continue;

      const { id, rawPrice } = product.selectedOption;
      data.push({
        id,
        quantity: 1,
        properties: {
          BoostBundle: '',
          _boostBundleId: dynamicBundleData.widgetId,
          _boostBundleType: dynamicBundleData.bundle_type,
          _boostItemPrice: rawPrice,
          _boostBundleProducts: _boostBundleProducts,
        },
      });
    }
  }
  const btnAddToCart = target?.closest(BUTTON_ADD_BUNDLE_TO_CART_SELECTOR);
  const ctaBtnText = btnAddToCart?.querySelector('span');
  const textAddBundleAddToCart = ctaBtnText.innerText || 'Add bundle to cart';

  const element = ctaBtnText || target;

  element.textContent =
    context.app?.translation?.productItem?.atcAddingToCartBtnLabel || 'Adding...';

  btnAddToCart.setAttribute('disabled', true);

  try {
    const res = await CartAPI.addMultiProductToCart(data);
    if (res.status === 200) {
      element.textContent =
        context.app?.translation?.productItem?.atcAddedToCartBtnLabel || 'Added!';

      const { enableCart, autoOpenAjaxCartOnAddToCart = true } =
        getThemeSettings(context)?.cart || {};
      const { noneCartOption = false } = window.boostWidgetIntegration || {};
      const shouldOpenCart = !noneCartOption && autoOpenAjaxCartOnAddToCart;

      /**
       * Communicate through event because ATC action handler belong to ProductGridFilter or RCM context
       * State from Cart context can't access other contexts and trigger re-render
       */
      if (enableCart) {
        const event = shouldOpenCart ? 'boost-sd-open-cart' : 'boost-sd-re-render-cart';
        window.dispatchEvent(new CustomEvent(event));
        closeDynamicBundleModal(context);
      } else {
        setWindowLocation(buildUrlWithLocalization('cart'));
      }
    } else {
      element.textContent =
        context.app?.translation?.productItem?.atcFailedToCartBtnLabel || 'Failed!';

      btnAddToCart.removeAttribute('disabled');
    }
  } catch {
    element.textContent =
      context.app?.translation?.productItem?.atcFailedToCartBtnLabel || 'Failed!';
  }

  setTimeout(function updateTextAddBundleToCart() {
    element.textContent = textAddBundleAddToCart;
    btnAddToCart.removeAttribute('disabled');
  }, 500);
};

/**
 * @type {import('./index.d').getTargetElementUpdate}
 */
const getTargetElementUpdate = (context) => {
  let targetElement = null;
  if (context.blockType === 'instantSearch') {
    targetElement = document
      ?.getElementById(`${PRE_FIX_CLASS_DYNAMIC_BUNDLE_MODAL}${context.blockType}`)
      ?.querySelector(MODAL_WRAPPER_SELECTOR);
  } else {
    if (isSearchPage(context)) {
      targetElement = document?.getElementById(DYNAMIC_BUNDLE_ID_CONTAINER_SEARCH_PAGE);
    }
  }

  return targetElement;
};

/**
 * @type {import('./index.d').updateDynamicBundleData}
 */
const updateDynamicBundleData = (context, widgetId, data, action, target) => {
  const [getDynamicBundleData] = context.useContextState(`${KEY_DYNAMIC_BUNDLE}-${widgetId}`, {});
  const oldDataDynamicBundle = getDynamicBundleData();

  if (!oldDataDynamicBundle) {
    console.error('Bundle data not found');
    return;
  }

  return dynamicBundleData(context, oldDataDynamicBundle, data, action, target);
};

/**
 * @type {import('./index.d').openDynamicBundleModal}
 */
const openDynamicBundleModal = async (context, widgetId) => {
  const bundleModal = document.getElementById(
    `${PRE_FIX_CLASS_DYNAMIC_BUNDLE_MODAL}${context.blockType}`
  );

  // If opening from ISW and ISW is inside a dialog (top layer),
  // move bundle modal next to ISW container to share the same top layer
  if (context.blockType === 'instantSearch') {
    const iswContainer = document.getElementById('bc-instant-search');
    const dialogParent = iswContainer?.closest('dialog');

    // Only move if ISW is inside a dialog element
    if (dialogParent && iswContainer && bundleModal) {
      // Insert as sibling to ISW, not as child
      if (bundleModal.parentElement !== dialogParent) {
        iswContainer.insertAdjacentElement('afterend', bundleModal);
      }

      // Add special class to identify this is a dialog case
      // Do this AFTER moving to ensure the class is on the element in its final position
      bundleModal.classList.add('boost-sd__modal--in-dialog');
    } else {
      // Remove the class if not in dialog mode
      bundleModal?.classList.remove('boost-sd__modal--in-dialog');
    }
  }

  const wrapperModal = bundleModal?.querySelector(MODAL_WRAPPER_SELECTOR);
  const dynamicBundleData = getDynamicBundleDataByWidgetId(context, widgetId, true);
  setSessionStorage(KEY_DYNAMIC_BUNDLE_OPENING, widgetId);

  renderDynamicBundleWithData(
    context,
    widgetId,
    dynamicBundleData,
    wrapperModal,
    BUNDLE_NAME_TEMPLATE.dynamic,
    function showModalDynamicBundle() {
      if (wrapperModal) {
        bundleModal.style.display = 'flex';
        const firstFocusable =
          bundleModal?.querySelector('[tabindex="0"]') ||
          bundleModal?.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
        if (firstFocusable) {
          firstFocusable.focus(); // Set focus on the first focusable element in the modal
        }
        BundleEventProvider.dispatchEvent('afterRender');
      }
    }
  );
};

/**
 * @type {import('./index.d').closeDynamicBundleModal}
 */
const closeDynamicBundleModal = (context) => {
  const widgetId = getSessionStorage(KEY_DYNAMIC_BUNDLE_OPENING);
  const bundleModal = document.getElementById(
    `${PRE_FIX_CLASS_DYNAMIC_BUNDLE_MODAL}${context.blockType}`
  );

  if (bundleModal) {
    bundleModal.style.display = 'none';
    if (isKeyboardActivated()) {
      const bundleBlock = document?.querySelector(
        `.boost-sd__product-bundle[data-widget-id = "${widgetId}"]`
      );
      bundleBlock?.focus(); // Return focus to the bundle block by keyboard
    }
  }
};

/**
 * @type {import('./index.d').removeClassDropdownListOpening}
 */
const removeClassDropdownListOpening = (dropdown) => {
  return dropdown?.classList?.remove(DROPDOWN_LIST_OPEN);
};

/**
 * @type {import('./index.d').closeAllDropdown}
 */
const closeAllDropdown = () => {
  const dropdowns = document?.querySelectorAll(`.${DROPDOWN_LIST}.${DROPDOWN_LIST_OPEN}`);

  dropdowns?.forEach(removeClassDropdownListOpening);
};

/**
 * @type {import('./index.d').toggleDropdown}
 */
const toggleDropdown = (target) => {
  const targetDropdown = target?.closest(`.${DROPDOWN_CONTAINER}`);
  const isOpen = targetDropdown?.querySelector(`.${DROPDOWN_LIST_OPEN}`);

  closeAllDropdown(); // Close all dropdown before new open other dropdown

  if (!isOpen) {
    targetDropdown?.querySelector(`.${DROPDOWN_LIST}`)?.classList?.add(DROPDOWN_LIST_OPEN);
  }
};

/**
 * @type {import('./index.d').selectVariantProduct}
 */
const selectVariantProduct = async (context, target, wrapper) => {
  const selectedVariantItem = target?.closest(DROPDOWN_ITEM_SELECTOR);
  const targetDropdown = target?.closest('.boost-sd__dropdown-dynamic');
  const targetButton = targetDropdown?.querySelector('.boost-sd__dropdown-selected-option');
  const clickedButton = targetButton?.getAttribute('id');
  if (clickedButton) {
    setSessionStorage(KEY_DYNAMIC_BUNDLE_COMBOBOX_BUTTON, clickedButton);
  }

  if (selectedVariantItem) {
    const variantId = selectedVariantItem.dataset?.variantId;
    const productId = target?.closest(BUNDLE_IMAGE_ITEM_SELECTOR)?.dataset?.productId;
    const widgetId = target?.closest(RECOMMENDATION_SELECTOR)?.dataset?.widgetId;

    const dynamicBundleData = updateDynamicBundleData(
      context,
      widgetId,
      { variantId, productId },
      BUNDLE_ACTION.changeVariant,
      target
    );
    renderDynamicBundleWithData(
      context,
      widgetId,
      dynamicBundleData,
      wrapper,
      BUNDLE_NAME_TEMPLATE.dynamic,
      function focusVariant() {
        if (isKeyboardActivated()) {
          const comboBoxButtonId = getSessionStorage(KEY_DYNAMIC_BUNDLE_COMBOBOX_BUTTON);
          const comboBoxButtonChange = document?.querySelector(`#${comboBoxButtonId}`);
          // Return focus to the checkbox by keyboard
          comboBoxButtonChange?.focus();
        }
      }
    );

    target?.closest(`.${DROPDOWN_LIST_OPEN}`)?.classList?.remove(DROPDOWN_LIST_OPEN);
  }
};

/**
 * @type {import('./index.d').toggleCheckbox}
 */
const toggleCheckbox = async (context, target, wrapper) => {
  const checkBoxProduct = target?.closest(CHECK_BOX_SELECTOR);
  const checkBoxButton = target?.closest(CHECK_BOX_BUTTON);
  const clickedCheckboxId = checkBoxButton?.getAttribute('id');
  if (clickedCheckboxId) {
    // Set session storage for only the clicked checkbox
    setSessionStorage(KEY_DYNAMIC_BUNDLE_CHECKBOX, clickedCheckboxId);
  }

  if (checkBoxProduct) {
    // re-render dynamic bundle
    const productId = target?.closest(BUNDLE_IMAGE_ITEM_SELECTOR)?.dataset?.productId;
    const widgetId = target?.closest(RECOMMENDATION_SELECTOR)?.dataset?.widgetId;

    const newDataDynamicBundle = await updateDynamicBundleData(
      context,
      widgetId,
      { productId },
      BUNDLE_ACTION.clickCheckbox,
      target
    );

    renderDynamicBundleWithData(
      context,
      widgetId,
      newDataDynamicBundle,
      wrapper,
      BUNDLE_NAME_TEMPLATE.dynamic,
      function focusCheckBox() {
        if (isKeyboardActivated()) {
          const checkboxId = getSessionStorage(KEY_DYNAMIC_BUNDLE_CHECKBOX);
          const checkboxChange = document?.querySelector(`#${checkboxId}`);
          // Return focus to the checkbox by keyboard
          checkboxChange?.focus();
        }
      }
    );
  }
};

/**
 * @type {import('./index.d').initBasicEventsForDynamicBundle}
 */
const initBasicEventsForDynamicBundle = (context, target, event, wrapper) => {
  // click change variants in dynamic
  selectVariantProduct(context, target, wrapper);

  // handle click checkbox
  toggleCheckbox(context, target, wrapper);

  // click select option - close other dropdown list and toggle this dropdown list
  if (target?.closest(`.${DROPDOWN_SELECT_OPTION}`)) {
    toggleDropdown(target);
  } else {
    // click outside select option - close all dropdown list
    closeAllDropdown();
  }

  // click outside dynamic bundle or click button close dynamic bundle then close
  if (
    (!target?.closest(RECOMMENDATION_SELECTOR) && !target?.closest('.boost-sd__product-bundle')) ||
    target?.closest('.boost-sd__modal-close-btn--dynamic-bundle')
  ) {
    closeDynamicBundleModal(context);
  }

  // handle click product item with dynamic bundle integrate with locale
  const classProductLink =
    'a.boost-sd__recommendation-bundle-link, a.boost-sd__recommendation-bundle-link-image';
  handleClickTagAItemBuildUrlWithLocale(event, classProductLink);
  // Dispatch custom event after checkbox click or change data is handled
  if (target?.matches("button[role='checkbox']") || target?.matches("li[role='option']")) {
    BundleEventProvider.dispatchEvent('afterRender');
  }
};

/**
 * @type {import('./index.d').filterProductAvailable}
 */
const filterProductAvailable = (product) => {
  return product?.available;
};

/**
 * @type {import('./index.d').filterProductSelected}
 */
const filterProductSelected = (product) => {
  return product?.checked;
};

/**
 * @type {import('./index.d').findSelectedVariant}
 */
const findSelectedVariant = (variant, product) => {
  return String(variant.id) === String(product.selectedOption?.id);
};

/**
 * @type {import('./index.d').generateUuid}
 */
export const generateUuid = () => {
  return 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'.replace(/[x]/g, function (c) {
    var r = (Math.random() * 6) | 0;
    return r.toString(16);
  });
};

/**
 * Utility functions handle navigate on combobox
 * @type {import('./index.d').handleComboboxNavigation}
 */
const handleComboboxNavigation = (event, context, container) => {
  const target = event.target;
  const combobox = target?.closest('.boost-sd__dropdown-dynamic');
  if (!combobox) return;

  const expanded = combobox.getAttribute('aria-expanded') === 'true';
  const comboboxList = combobox?.querySelector('[role="listbox"]');
  const comboboxItems = comboboxList?.querySelectorAll('[role="option"]');
  let selectedIndex = Array.from(comboboxItems).findIndex((item) =>
    item.classList.contains(DROPDOWN_SELECT_OPTION_SELECTED)
  );

  const isArrowUp = event.key === 'ArrowUp';
  const isArrowDown = event.key === 'ArrowDown';
  const isEscPressed = event.key === 'Escape' || event.keyCode === 27;

  if (expanded && (isArrowUp || isArrowDown)) {
    event.preventDefault();
    selectedIndex = isArrowDown
      ? (selectedIndex + 1) % comboboxItems.length
      : (selectedIndex - 1 + comboboxItems.length) % comboboxItems.length;
    comboboxItems.forEach((item) => item.classList.remove(DROPDOWN_SELECT_OPTION_SELECTED));
    comboboxItems[selectedIndex].classList.add(DROPDOWN_SELECT_OPTION_SELECTED);
    combobox.setAttribute('aria-activedescendant', comboboxItems[selectedIndex].id);
    comboboxItems[selectedIndex].scrollIntoView({ block: 'nearest' });
    comboboxItems[selectedIndex].focus();
  }

  if (expanded && KEYS.includes(event.key)) {
    selectVariantProduct(context, target, container);
  }

  if (expanded && isEscPressed) {
    combobox.setAttribute('aria-expanded', 'false');
    closeAllDropdown();
  }
};

/**
 * @type {import('./index.d').addEventListenersToCombobox}
 */
const addEventListenersToCombobox = (element, context) => {
  element.addEventListener('keydown', (event) => handleComboboxNavigation(event, context, element));
};

/**
 * @type {import('./index.d').handleDropdownToggle}
 */
const handleDropdownToggle = (target, combobox) => {
  if (target?.closest(`.${DROPDOWN_SELECT_OPTION}`)) {
    combobox?.setAttribute('aria-expanded', 'true');
    toggleDropdown(target);
  } else {
    combobox?.setAttribute('aria-expanded', 'false');
    closeAllDropdown();
  }
};

/**
 * Event listener for clicks inside the bundle modal
 * @type {import('./index.d').handleModalEvents}
 */
const handleModalEvents = (event, context) => {
  const target = event.target;
  initBasicEventsForDynamicBundle(context, target, event);

  if (target?.closest(BUTTON_ADD_BUNDLE_TO_CART_SELECTOR)) {
    addBundleToCart(context, target);
  }

  if (
    (!target?.closest(RECOMMENDATION_SELECTOR) && !target?.closest('.boost-sd__product-bundle')) ||
    target?.closest('.boost-sd__modal-close-btn--dynamic-bundle')
  ) {
    closeDynamicBundleModal(context);
  }
};

/**
 * Main function to register events
 * @type {import('./index.d').registerEventBundle}
 */
const registerEventBundle = (context, targetElement) => {
  if (!targetElement || !(targetElement instanceof HTMLElement)) return;
  // Idempotency guard to avoid stacking duplicate listeners on re-renders
  if (targetElement.dataset.bundleEventsBound === 'true') return;
  targetElement.dataset.bundleEventsBound = 'true';

  targetElement.addEventListener('click', (event) => {
    const target = event.target;
    initBasicEventsForDynamicBundle(context, target, event, targetElement);

    if (target?.closest(BUTTON_ADD_BUNDLE_TO_CART_SELECTOR)) {
      addBundleToCart(context, target);
    }
  });

  targetElement.addEventListener('keydown', (event) => {
    handleComboboxNavigation(event, context, targetElement);

    const target = event.target;
    if (KEYS.includes(event.key)) {
      if (target?.closest(BUTTON_ADD_BUNDLE_TO_CART_SELECTOR)) {
        addBundleToCart(context, target);
      }

      const combobox = target?.closest('.boost-sd__dropdown-dynamic');
      handleDropdownToggle(target, combobox);
    }
  });
};

/**
 * @type {import('./index.d').registerEventBundleModal}
 */
const registerEventBundleModal = (context, domEvent) => {
  // create div bundle Modal dialog
  createDivBundleModal(context);

  if (!domEvent || !(domEvent instanceof HTMLElement)) return;
  if (domEvent.dataset.bundleEventsBound === 'true') return;
  domEvent.dataset.bundleEventsBound = 'true';

  domEvent?.addEventListener('click', function (event) {
    const target = event.target;

    initBasicEventsForDynamicBundle(context, target, event);

    if (target?.closest(DYNAMIC_BUNDLE_SHOW_POPUP_SELECTOR)) {
      const widgetId = target?.closest(RECOMMENDATION_SELECTOR)?.dataset?.widgetId;
      openDynamicBundleModal(context, widgetId);
    }
  });
};

/**
 * @type {import('./index.d').getDynamicBundleSettingsByWidgetId}
 */
export const getDynamicBundleSettingsByWidgetId = (context, widgetId) => {
  let widgetDesignSettings =
    context?.app?.recommendationWidgets?.['dynamic-bundle']?.[widgetId]?.widgetDesignSettings || {};

  const dynamicDefaultSettings =
    context?.app?.recommendationWidgets?.dynamicBundleDefaultSettings ||
    DYNAMIC_BUNDLE_DEFAULT_SETTINGS;

  widgetDesignSettings = {
    ...dynamicDefaultSettings,
    ...widgetDesignSettings,
  };

  // Split font style to font style and font weight
  const { titleFontStyle } = widgetDesignSettings;
  const { fontWeight, fontStyle } = splitGoogleFontStyle(titleFontStyle);

  return {
    widgetDesignSettings: {
      ...widgetDesignSettings,
      titleFontStyle: fontStyle,
      titleFontWeight: fontWeight,
    },
  };
};

/**
 * @type {import('./index.d').dynamicBundleData}
 */
export const dynamicBundleData = (context, bundle, optionData, action, target) => {
  const [getDynamicBundleData, setDynamicBundleData] = context.useContextState(
    `${KEY_DYNAMIC_BUNDLE}-${bundle.widgetId}`,
    {}
  );
  const recommendationData = getDynamicBundleData();
  let { products = bundle.products } = recommendationData;

  // because bundle_type starts with ai will change product each time
  if (bundle.bundle_type?.startsWith('ai_')) {
    products = bundle.products;
  }

  let totalPrice = 0;
  let totalBundlePrice = 0;

  // set checked product
  for (const product of products) {
    let data = product;
    product.id = Number(product.id);

    if (product.available) {
      if (!action || action === BUNDLE_ACTION.initData) {
        product.checked = true;
      } else if (
        action === BUNDLE_ACTION.clickCheckbox &&
        String(product.id) === String(optionData.productId)
      ) {
        product.checked = !product.checked;
      }
    } else product.checked = false;

    if (product.variants.length > 0) {
      // only get variants available
      product.variants = product.variants.filter(filterProductAvailable);

      // if have choose then keep before selected
      if (product.selectedOption?.id) {
        data = product.variants.find((variant) => findSelectedVariant(variant, product));
      } else {
        // if have not choose then default select variant with min price
        data = product.variants.reduce((minVariant, currentVariant) =>
          parseFloat(currentVariant.price) < parseFloat(minVariant.price)
            ? currentVariant
            : minVariant
        );
      }
    }

    if (
      action === BUNDLE_ACTION.changeVariant &&
      optionData?.productId &&
      optionData.variantId &&
      product.variants.length > 0 &&
      String(optionData?.productId) === String(product.id)
    ) {
      // change select data variant
      data = product.variants.find(
        (variant) => String(variant.id) === String(optionData.variantId)
      );
    }

    const { price: originPrice = 0, title, id } = data;

    product.selectedOption = {
      rawPrice: Number(originPrice || 0),
      title,
      price: formatPrice(context, originPrice),
      originPrice: formatPrice(context, originPrice),
      minPrice: formatPrice(context, product.price_min),
      id,
    };

    if (product.checked) {
      totalPrice += +originPrice;
    }
  }
  // Check if all elements in the products array have checked === true
  const isSaleChecked = products.every(filterProductSelected) && bundle.discount_value > 0;

  // calculate discount for product selected
  if (isSaleChecked) {
    const currencyRate = Number(window.Shopify?.currency?.rate || 1);
    if (bundle.discount_type === 'percent') {
      totalBundlePrice = totalPrice - (totalPrice * bundle.discount_value) / 100;
    } else {
      if (totalPrice >= bundle.discount_value * currencyRate) {
        totalBundlePrice = Math.abs(totalPrice - bundle.discount_value * currencyRate);
      } else {
        totalBundlePrice = 0;
      }
    }
  } else {
    totalBundlePrice = totalPrice;
  }

  const hasSelectedProduct =
    products.length > 0 && products.filter(filterProductAvailable).length > 0;

  // sort current product first
  if (bundle.currentProductId) {
    const index = products.findIndex((product) => product.id === bundle.currentProductId);
    if (index !== -1) {
      const [product] = products.splice(index, 1);
      products.unshift(product);
    }
  }

  bundle.products = products;
  bundle.rawTotalPrice = totalPrice;
  bundle.rawTotalBundlePrice = totalBundlePrice;
  bundle.totalPrice = formatPrice(context, totalPrice);
  bundle.totalBundlePrice = formatPrice(context, totalBundlePrice);
  const currencyRate = Number(window.Shopify?.currency?.rate || 1);
  bundle.amountValueDisplay =
    bundle.discount_type === 'percent'
      ? bundle.discount_value + `%`
      : bundle.discount_value === 0
        ? 0
        : formatPrice(context, Math.abs(bundle.discount_value * currencyRate));

  bundle.hasSelectedProduct = hasSelectedProduct;
  // hardcode style2 - currently only support style
  bundle.bundleStyle = 'style2';

  setDynamicBundleData(bundle);

  if (target?.closest(`#${PRE_FIX_CLASS_DYNAMIC_BUNDLE_MODAL}${context.blockType}`)) {
    bundle.isPopupDynamicBundleOpening = true;
  }

  const oldBundleDataSession = getSessionStorage(KEY_DYNAMIC_BUNDLE_DATA_SESSION) || {};

  const newBundleDataSession = {
    ...oldBundleDataSession,
    [bundle.widgetId]: {
      widgetId: bundle.widgetId,
      bundle_type: bundle.bundle_type,
      recommendationType: bundle.recommendation_type,
      primary_algorithm: bundle.primary_algorithm || '',
      secondary_algorithm: bundle.secondary_algorithm || '',
      amountValueDisplay: bundle.amountValueDisplay || '',
      totalBundlePrice: bundle.totalBundlePrice || 0,
      bundleProductIds: bundle?.products?.map((product) => product.id),
    },
  };

  setSessionStorage(KEY_DYNAMIC_BUNDLE_DATA_SESSION, newBundleDataSession);

  return bundle;
};

/**
 * @type {import('./index.d').focusOnSearchElements}
 */
export function focusOnSearchElements() {
  const searchIcon = document?.querySelector(`
    .logo-area__right .show-search-link,
    .header__icon--search,
    .header__icon-wrapper,
    .js-search-sidebar-link,
    .js-search-header,
    .header__secondary-nav a[href="/search"],
    .site-header__links .search-button,
    .header-navs .js-search-trigger,
    .header--search,
    .navlink--search,
    .js-modal-open-search-modal,
    .slideout__trigger-search
  `);
  const searchInput = document?.querySelector('.boost-sd__search-form-input');
  const initInput = document?.querySelector(
    '.boost-sd__search-widget-init-input[aria-expanded="true"]'
  );

  if (searchInput) {
    searchInput.focus();
  } else if (initInput) {
    initInput.focus();
  } else if (searchIcon) {
    searchIcon?.focus();
  } else {
    window.scrollTo({
      top: 10,
      behavior: 'smooth',
    });
    const skipToContentLink = Array.from(document?.querySelectorAll('a')).find((link) =>
      link.textContent.includes('Skip to content')
    );
    if (skipToContentLink) {
      skipToContentLink.focus();
    }
  }
}

/**
 * @type {import('./index.d').closeModalAndFocus}
 */
function closeModalAndFocus(context, idModalByBlock) {
  closeDynamicBundleModal(context);

  if (idModalByBlock === 'boost-sd__dynamic-bundle-modal-instantSearch') {
    focusOnSearchElements();
  }
}

/**
 * @type {import('./index.d').createDivBundleModal}
 */
export const createDivBundleModal = (context) => {
  const idModalByBlock = `${PRE_FIX_CLASS_DYNAMIC_BUNDLE_MODAL}${context.blockType}`;

  let bundleModal = document.getElementById(idModalByBlock);

  if (!bundleModal) {
    const dynamicModal = document.createElement('div');
    dynamicModal.id = idModalByBlock;
    dynamicModal.className = 'boost-sd__modal';
    dynamicModal.setAttribute('ariaModal', 'true');
    dynamicModal.setAttribute('role', 'dialog');
    dynamicModal.setAttribute('ariaLabel', 'modal');

    const backdrop = document.createElement('div');
    backdrop.className = 'boost-sd__modal-backdrop';

    const modalContainer = document.createElement('div');
    modalContainer.className = 'boost-sd__modal-container';
    modalContainer.style.maxWidth = '1000px';

    const modalWrapper = document.createElement('div');
    modalWrapper.className = 'boost-sd__modal-wrapper';
    modalWrapper.setAttribute('tabIndex', '-1');

    modalContainer.appendChild(modalWrapper);

    dynamicModal.appendChild(backdrop);
    dynamicModal.appendChild(modalContainer);

    dynamicModal.style.display = 'none';

    document.body.appendChild(dynamicModal);

    bundleModal = dynamicModal;
  }

  // add event for bundle
  bundleModal?.addEventListener('click', function (event) {
    // If bundle modal is in dialog mode (has the special class), stop event propagation
    // to prevent ISW's click outside handler from closing ISW
    if (bundleModal.classList.contains('boost-sd__modal--in-dialog')) {
      event.stopPropagation();
    }

    handleModalEvents(event, context);
  });

  // navigate by keydown
  bundleModal?.addEventListener('keydown', (event) => {
    const target = event.target;
    const combobox = target?.closest('.boost-sd__dropdown-dynamic');
    if (combobox) {
      const expanded = combobox?.getAttribute('aria-expanded') === 'true';
      const comboboxList = combobox?.querySelector('[role="listbox"]');

      const comboboxItems = comboboxList?.querySelectorAll('[role="option"]');
      let selectedIndex = Array?.from(comboboxItems)?.findIndex((item) =>
        item.classList?.contains(DROPDOWN_SELECT_OPTION_SELECTED)
      );

      const isArrowUp = event.key === 'ArrowUp';
      const isArrowDown = event.key === 'ArrowDown';
      const isEscPressed = event.key === 'Escape' || event.keyCode === 27;

      // Handle ArrowUp/ArrowDown for navigating options
      if (expanded && (isArrowUp || isArrowDown)) {
        event.preventDefault();
        if (isArrowDown) {
          selectedIndex = (selectedIndex + 1) % comboboxItems.length;
        } else if (isArrowUp) {
          selectedIndex = (selectedIndex - 1 + comboboxItems.length) % comboboxItems.length;
        }
        // Update selected option
        comboboxItems.forEach((item) => item.classList.remove(DROPDOWN_SELECT_OPTION_SELECTED));
        comboboxItems[selectedIndex].classList.add(DROPDOWN_SELECT_OPTION_SELECTED);
        combobox.setAttribute('aria-activedescendant', comboboxItems[selectedIndex].id);
        // Ensure the selected item is focused and visible
        comboboxItems[selectedIndex].scrollIntoView({ block: 'nearest' });
        comboboxItems[selectedIndex].focus();
      }

      // Handle selecting an option with Enter/Space when the dropdown is expanded
      if (expanded && KEYS.includes(event.key)) {
        const target = event.target;
        initBasicEventsForDynamicBundle(context, target, event);
      }

      // Handle closing the dropdown with Escape
      if (expanded && isEscPressed) {
        combobox.setAttribute('aria-expanded', 'false');
        closeAllDropdown();
      }
    }

    if (KEYS.includes(event.key)) {
      if (target?.closest(BUTTON_ADD_BUNDLE_TO_CART_SELECTOR)) {
        addBundleToCart(context, target, context.document);
      }

      // Handle Enter/Space to toggle dropdown
      handleDropdownToggle(target, combobox);

      // click outside dynamic bundle or click button close dynamic bundle then close
      if (target?.closest('.boost-sd__modal-close-btn--dynamic-bundle')) {
        closeModalAndFocus(context, idModalByBlock);
      }
    }

    if (event.key === 'Escape') {
      closeModalAndFocus(context, idModalByBlock);
    }
  });
};

/**
 * @type {import('./index.d').initDynamicBundle}
 */
export const initDynamicBundle = (context) => {
  if (!isValidateDynamicBundle(context)) return;

  const isBlockShowModal = context.blockType === 'instantSearch';

  if (isBlockShowModal) {
    registerEventBundleModal(context, context.document);
  }
};

/**
 * @type {import('./index.d').renderOneBundleByTargetElement}
 */
const renderOneBundleByTargetElement = async (context, targetBundlePage, bundle) => {
  const dataDynamicBundle = dynamicBundleData(
    context,
    bundle ? bundle : context?.dynamicBundles[0],
    {},
    BUNDLE_ACTION.initData
  );
  const templateDynamicBundle = await getDynamicBundleWidgetTemplate(context);

  if (targetBundlePage) {
    targetBundlePage.innerHTML = await context.templateRender(templateDynamicBundle, {
      bundle: dataDynamicBundle,
      ...getDynamicBundleSettingsByWidgetId(context, dataDynamicBundle?.widgetId),
      locale: (window?.Shopify?.routes?.root || '/')?.replace(/\/$/, ''),
    });

    registerEventBundle(context, targetBundlePage);
    trapFocusInBundle(targetBundlePage, context, false);
    BundleEventProvider.dispatchEvent('afterRender');
  }
};

/**
 * @type {import('./index.d').renderRecommendationForSearchPage}
 */
export const renderRecommendationForSearchPage = async (context) => {
  const domDynamicBundleSearchPage = document?.getElementById(
    DYNAMIC_BUNDLE_ID_CONTAINER_SEARCH_PAGE
  );

  if (domDynamicBundleSearchPage) {
    if (context?.dynamicBundles?.length > 0) {
      // Can use customize settings trigger for multiple bundles
      if (window.boostSdMultipleBundle) {
        // reset element prevent push multiple when filter tree call two 2 times in first load.
        domDynamicBundleSearchPage.innerHTML = '';

        context?.dynamicBundles.forEach((bundle) => {
          if (
            bundle.bundle_type !== 'volume' &&
            (bundle?.bundle_display_type === 'bottom' || !bundle?.bundle_display_type)
          ) {
            const targetBundlePage = document.createElement('div');
            targetBundlePage.id = `boost-sd__bundle-search-page-${bundle.widgetId}-${generateUuid()}`;
            domDynamicBundleSearchPage?.appendChild(targetBundlePage);

            renderOneBundleByTargetElement(context, targetBundlePage, bundle);
          }
        });
      } else {
        const bundleBottom = context?.dynamicBundles?.find(
          (bundle) =>
            bundle.bundle_type !== 'volume' &&
            (bundle?.bundle_display_type === 'bottom' || !bundle?.bundle_display_type)
        );

        bundleBottom &&
          renderOneBundleByTargetElement(context, domDynamicBundleSearchPage, bundleBottom);
      }
    }
  }

  const domEmbeddedBundleProductList = document?.getElementById(
    EMBEDDED_BUNDLE_ID_CONTAINER_PRODUCT
  );

  const bundleTypeProduct = context?.dynamicBundles?.find(
    (bundle) => bundle?.bundle_display_type === 'product'
  );

  if (domEmbeddedBundleProductList && bundleTypeProduct) {
    const dataDynamicBundle = dynamicBundleData(
      context,
      bundleTypeProduct,
      {},
      BUNDLE_ACTION.initData
    );

    const templateEmbeddedBundle = await getDynamicBundleWidgetTemplate(
      context,
      BUNDLE_NAME_TEMPLATE.embedded
    );

    domEmbeddedBundleProductList.innerHTML = await context.templateRender(templateEmbeddedBundle, {
      bundle: dataDynamicBundle,
      ...getDynamicBundleSettingsByWidgetId(context, dataDynamicBundle?.widgetId),
    });

    registerEventBundleModal(context, domEmbeddedBundleProductList);
    setUniformProductImageHeight(
      context,
      context.app.templateSettings?.themeSettings?.productList?.productsPerRowOnMobile || 2,
      context.app.templateSettings?.themeSettings?.productList?.productsPerRowOnDesktop || 3,
      context.app.generalSettings?.breakpointmobile || 575,
      context.app.generalSettings?.breakpointtabletportraitmax || 991,
      3
    );
    BundleEventProvider.dispatchEvent('afterRender');
  }
};

/**
 * @type {import('./index.d').renderPredictiveBlocksForProductPage}
 */
export const renderPredictiveBlocksForProductPage = async (context, productId) => {
  context.widgetId = 'bc-predictive-bundle';
  const domPredictiveBundles = document?.querySelectorAll(PREDICTIVE_BUNDLE_BLOCK_SELECTOR);

  if (!domPredictiveBundles.length) return;

  const getDynamicBundles = await predictiveBundleByProductId(context, productId);
  if (!getDynamicBundles.length) return;

  if (window.boostSdMultipleBundle === true) {
    domPredictiveBundles.forEach((domPredictiveBundle) => {
      getDynamicBundles.forEach((bundle) => {
        const targetBundle = document.createElement('div');
        targetBundle.id = `boost-sd__bundle-predictive-${bundle.widgetId}-${generateUuid()}`;
        domPredictiveBundle.appendChild(targetBundle);
        if (bundle.bundle_type !== 'volume') {
          renderOneBundleByTargetElement(context, targetBundle, bundle);
        }
      });
    });
  } else {
    getDynamicBundles[0].currentProductId = productId;
    const dataDynamicBundle = dynamicBundleData(
      context,
      getDynamicBundles[0],
      {},
      BUNDLE_ACTION.initData
    );
    const templateDynamicBundle = await getDynamicBundleWidgetTemplate(context);
    const domBundleInnerHtml = await context.templateRender(templateDynamicBundle, {
      bundle: dataDynamicBundle,
      ...getDynamicBundleSettingsByWidgetId(context, dataDynamicBundle.widgetId),
      locale: (window?.Shopify?.routes?.root || '/')?.replace(/\/$/, ''),
    });

    domPredictiveBundles.forEach((domBundle) => {
      if (dataDynamicBundle.bundle_type !== 'volume') {
        domBundle.innerHTML = domBundleInnerHtml;
        registerEventBundle(context, domBundle);
        addEventListenersToCombobox(domBundle, context);
        trapFocusInBundle(domBundle, context, false);
        BundleEventProvider.dispatchEvent('afterRender');
      }
    });
  }
};

/**
 * @type {import('./index.d').handleClickEmbeddedBundle}
 */
export const handleClickEmbeddedBundle = (context, target) => {
  const widgetId = target?.closest('.boost-sd__product-bundle')?.getAttribute('data-widget-id');
  widgetId && openDynamicBundleModal(context, widgetId);
};

/**
 * Support WCAG : Function to trap focus inside the modal and close with Escape
 * @type {import('./index.d').trapFocusInBundle}
 */
export function trapFocusInBundle(bundleWrapper, context, isModal) {
  const focusableElements = bundleWrapper?.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  const firstFocusableElement = focusableElements[0];
  const lastFocusableElement = focusableElements[focusableElements.length - 1];

  // Add an event listener to manage tabbing and enter key actions
  bundleWrapper.addEventListener('keydown', function (e) {
    const isTabPressed = e.key === 'Tab';
    if (isModal && isTabPressed) {
      if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          firstFocusableElement.focus();
          e.preventDefault();
        }
      }
    }
  });
}

window.addEventListener('boost-sd-open-dynamic-bundle', function openDynamicModal(e) {
  if (e?.detail?.widgetId && e?.detail?.context)
    openDynamicBundleModal(e.detail.context, e.detail.widgetId);
});
